module Meevo.Plugins.Client {
    "use strict";

    /**
    * CreditCardSection Section -- need aggregate model for handling functionality.
    */
    export class CreditCardSection extends Meevo.Components.BaseClasses.DataMaintenanceSectionBase<Model.Client> {
        protected clientCards: any;
        protected terminals: Meevo.Model.MerchantTerminal[] = [];
        protected allTerminals: Meevo.Model.MerchantTerminal[] = [];
        protected selectedTerminal: Meevo.Model.MerchantTerminal;
        protected areActiveTerminals: boolean = true;
        protected defaultMerchantAccount: Meevo.Model.MerchantAccount;
        protected currentMerchantAccount: Meevo.Model.MerchantAccount;
        protected isHostedPageAllowed: boolean = true;
        protected isManualEntryAllowed: boolean = true;

        private hasHsaFsaMerchantAccount: boolean;
        private readonly hostedPageId: Meevo.Model.HostedPageIdType = 'HostedPageClientCreditCard';

        public get auditNoun(): Meevo.Model.NounEnum {
            return Meevo.Model.NounEnum.CreditCard;
        }

        constructor(
            sectionId: number,
            displayNameContentKey: string,
            template: string,
            isPrimarySection: boolean,
            handlesOwnCrud: boolean,
            baseEndpoint: string,
            sectionEndpoint: string,
            loggingService: Meevo.Components.Logging.ILoggingService,
            protected dataService: Meevo.Components.BaseClasses.IDataService,
            protected modalService: Meevo.Components.Modal.IModalService,
            protected $q: ng.IQService,
            protected $translate: ng.translate.ITranslateService,
            protected identityService: Meevo.Components.Common.IIdentityService,
            noun: Meevo.Model.NounEnum = null,
            protected appSettings: Meevo.Components.Common.IAppSettings,
            securityService: Meevo.Components.Common.ISecurityService,
            protected hostedPageService: Meevo.Components.HostedPage.IHostedPageService,
            sectionSecurityKey?: string
        ) {
            super(sectionId, displayNameContentKey, template, isPrimarySection, handlesOwnCrud, baseEndpoint, sectionEndpoint, loggingService, dataService, identityService, noun, appSettings, sectionSecurityKey);

            securityService.checkSecurityKey(Meevo.Components.Common.SecurityKeys.REGISTER_REGISTER_ACTION_HOSTEDPAGE).then(result => {
                this.isHostedPageAllowed = result !== Model.SecurityAccessEnum.Hide;                 
            });
        }

        selected(): void {
            this.hostedPageService.reset(this.hostedPageId);
            this.getDefaultMerchantAccount().then(() => 
            {
                this.getTerminals();
            });
        }

        terminalChanged(item) {
            if (!item.originalMerchantAccountId) {
                item.originalMerchantAccountId = item.merchantAccountId;
            }
            if (item.merchantTerminalId == "") {
                item.merchantAccountId = item.originalMerchantAccountId;
            }
            else {
                item.merchantAccountId = _(this.terminals).findWhere({ id: item.merchantTerminalId }).merchantAccountId;
            }
        }

        private getTerminals() {
            const request = { 
	            criteria: {
                    isActiveOnly: true,
                    isHostedMode: this.isHostedPageAllowed,
	            }
            };

            const endPointUrl = `${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.merchantAccount}/Devices/List`;

            this.dataService.customMethodData(endPointUrl, "POST", request).then((response: any) => 
            {
                this.allTerminals = response.data;
            });
        }

        refreshTerminals(item) {
            return this.getCurrentMerchantAccount(item).then((merchantAccount) => {
                this.isManualEntryAllowed = merchantAccount.directManualEntry;

                const request = {
                    criteria: {
                        isActiveOnly: true,
                        isHostedMode: this.isHostedPageAllowed,
                    }
                };

                const endPointUrl = `${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.merchantAccount}/${merchantAccount.id}/Devices/List`;

                return this.dataService.customMethodData(endPointUrl, "POST", request).then((response: any) => {
                    this.terminals = _(response.data)
                        .map((terminal: any) => {
                            if (terminal.merchantTerminalType === Meevo.Model.MerchantTerminalTypeEnum.Hosted) {
                                terminal.displayName = this.$translate.instant('LABEL_HOSTED_PAGE');
                            }
                            return terminal;
                        });
                    item.terminals = this.terminals;
                    this.areActiveTerminals = this.terminals.length > 0;

                    return this.$q.resolve();
                });
            });
        }
        
        getSelectedTerminal(item) {
            const selectedTerminal = _(this.terminals).find(terminal => terminal.id === item.merchantTerminalId);

            if (!selectedTerminal) {
                return null;
            }

            return selectedTerminal;    
        }

        useHostedPage(item) {
            if (!this.currentMerchantAccount) {
                return false;
            }

            const selectedTerminal = this.getSelectedTerminal(item);

            if (!selectedTerminal) {
                return false;
            }

            return this.currentMerchantAccount.hasHostedPageSupport && selectedTerminal.merchantTerminalType === Meevo.Model.MerchantTerminalTypeEnum.Hosted;
        }

        private getDefaultMerchantAccount() {
            const request = {
	            criteria: {
		            getDefaultAccountOnly: false
	            }
            };

            const endPointUrl = `${this.appSettings.API_ROOT}/${this.appSettings.API_ENDPOINTS.merchantAccount}/List`;

            return this.dataService.customMethodData(endPointUrl, "POST", request).then((response: any) => {
                if (response.data && response.data.length > 0) {
	                const merchantAccount: Model.MerchantAccount = response.data.find(account => account.isDefault);
                    this.hasHsaFsaMerchantAccount = !!response.data.find(account => 
                        account.isHsaFsa);

	                return this.getSpecificMerchantAccount(merchantAccount.id).then((response: any) => {
                        this.defaultMerchantAccount = response;
                        this.currentMerchantAccount = response;
                    });
	            }
            });
        }

        private getCurrentMerchantAccount(item) {
            if (item.id) {
                return this.getSpecificMerchantAccount(item.merchantAccountId).then((merchantAccount: any) => {
                    this.currentMerchantAccount = merchantAccount;
                    return this.$q.resolve(this.currentMerchantAccount);
                });
            }

            if (!item.posStationDrawer) {
                this.currentMerchantAccount = this.defaultMerchantAccount;
                return this.$q.resolve(this.defaultMerchantAccount);
            }

            let merchantAccountId = item.posStationDrawer.merchantAccountId;
            if (!merchantAccountId) {
                this.currentMerchantAccount = this.defaultMerchantAccount;
                return this.$q.resolve(this.defaultMerchantAccount);
            }

            return this.getSpecificMerchantAccount(merchantAccountId).then((merchantAccount: any) => {
                this.currentMerchantAccount = merchantAccount;
                return this.$q.resolve(this.currentMerchantAccount);
            });
        }

        private getSpecificMerchantAccount(merchantAccountId) {
            return this.dataService.getItem(this.appSettings.API_ENDPOINTS.merchantAccount, new Guid(merchantAccountId));
        }        

        hostedPageClose(vm) {
            this.hostedPageService.reset(this.hostedPageId);

            try {
                vm.parentScope.runInitSearch();
                vm.parentScope.cancel(false);
            }
            catch(e) { }
        }

        hostedPageCancel(vm) {
            let promise = vm.item.id ? this.dataService.customMethodData(`/api/paymentaccount/${vm.item.id}`,
                "delete", {
                isHostedPageSetup: true
            }) : this.$q.resolve();
            promise.finally(() => {
                this.hostedPageClose(vm);
            })
        }


        hostedPageError(response, vm) {
            if (response.status == 409) { // conflict
                this.hostedPageService.reset(this.hostedPageId);
                this.modalService.alert(response.data.responseStatus.message);
            }
            try {
                vm.parentScope.cancel(false);
            }
            catch (e) { }
        }

        isHostedPageActive() {
            return this.hostedPageService.getHostedPageActive(this.hostedPageId);
        }

        hostedPageSave(item, vm): any {
            const request: Meevo.Components.HostedPage.IHostedPageRequest = {
                paymentAccountId: item.id,
                merchantAccountId: this.currentMerchantAccount.id,
                merchantProviderId: this.currentMerchantAccount.merchantProviderId,
	            isCardOnFile: true,
	            clientId: this.parentItemId,
	            externalPaymentAccountId: item.externalPaymentAccountId,
	            cardHolderName: item.cardholderName,
                billingPostalCode: item.zipCode,
                paymentAccountTokenCreatedCallback: (paymentAccountId) => {
                    item.id = paymentAccountId;
                },
                isHsaFsa: item.isHsaFsa,
                hostedPageIntent: Model.HostedPageIntent.RetailKeyed
            };

            const deferred = this.$q.defer();

            this.hostedPageService.process(request, this.hostedPageId)
                .then((response: any) => {
                    this.hostedPageClose(vm);
                    deferred.resolve(response);
                })
                .catch((response: any) => {
                    this.hostedPageError(response, vm);
                    deferred.reject();
                });

            return deferred.promise;
        }

        public onPerformAction(item: any, canEditModelEvenWhenRestricted: boolean, actionOnItem: { (item: any): void }) {
            if (canEditModelEvenWhenRestricted) {
                actionOnItem(item);
            }
            else {
                this.modalService.alert("MESSAGE_CANNOT_EDIT_RESTRICTED_CLIENT");
            }
        }

        public isForceManualEntryVisible(item) {
            // don't show if manual entry is not allowed
            if (!this.isManualEntryAllowed) {
                return false;
            }
            if (!item) { 
                return false;
            }
            // don't show on Edit screen
            if (item.externalPaymentAccountId) {
                return false;
            }
            // don't show if no terminal selected or selected terminal does not exist in list
            if (!item.merchantTerminalId || !this.terminals.find(terminal => terminal.id === item.merchantTerminalId)) {
                return false;
            }
            // don't show if hosted page
            if (this.useHostedPage(item)) {
                return false;
            }
            return true;
        }

        handleErrors(data) {
            if (data && data.isExternalUpdateFailed) {
                this.modalService.message("MESSAGE_EXTERNAL_UPDATE_FAILED");
            }
        }
    }
}